import React, { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';

import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Text, TextType } from 'ui-components/components/text';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { Badge } from '@mantine/core';

import { AutomationRulesContext } from '../../context';
import { SaveButton } from './save-button';
import EditorBody from './editor-body';
import { AutomationRulesSteps, useTrackAutomationRulesWorkflow } from '../../hooks/use-track-automation-rules-workflow';
import { UnsupportedRuleBanner } from './unsupported-rule-banner';
import { MissingWarehouseBanner } from './missing-warehouse-banner';

import messages from './messages';
import style from './style.module.less';

export const RuleEditorDrawer = () => {
  const intl = useIntl();
  const { isDrawerLoading, setIsDrawerOpen, selectedRule, openDeleteModal, hasAccess } = useContext(AutomationRulesContext);
  const { isNew, automationRuleId } = selectedRule ?? {};
  const trackCancel = useTrackAutomationRulesWorkflow(AutomationRulesSteps.CANCEL);

  const onDeleteRule = useCallback(() => {
    setIsDrawerOpen(false);
    openDeleteModal(automationRuleId ?? '');
  }, [openDeleteModal, automationRuleId, setIsDrawerOpen]);

  const onCloseDrawer = useCallback(() => {
    trackCancel({
      selectedRule,
      isAiGenerated: selectedRule?.isAiGenerated || false,
    });
    setIsDrawerOpen(false);
  }, [setIsDrawerOpen, trackCancel, selectedRule]);

  return (
    <Drawer
      confirmCancelBodyMessage={messages.automationRulesConfirmCancelBody}
      confirmCancelButtonMessage={messages.automationRulesConfirmCancelButton}
      confirmCancelHeaderMessage={messages.automationRulesConfirmCancelHeader}
      onCancel={onCloseDrawer}
      onClose={onCloseDrawer}
    >
      <DrawerHeader
        closeButtonAriaLabel={messages.closeDrawerAriaLabel}
        title={<Text textType={TextType.H4} value={messages.ruleEditorHeader} />}
      />
      <div className={style['drawer-body']}>
        <FlexColumn className={style['flow-container']}>
          <FlexRow className={style['flow-step-box']} gap={Gap.Space12}>
            <Badge color='neutral' size='sm' variant='outline'>
              {intl.formatMessage(messages.flowWhenBadge)}
            </Badge>
            <Text textType={TextType.Paragraph} value={messages.flowOrderImported} />
            <FontAwesomeIcon className={style['flow-step-check']} icon={faCircleCheck} />
          </FlexRow>
          <FlexColumn className={style['flow-connector']}>
            <div className={style['flow-connector-line']} />
          </FlexColumn>
          <div className={style['form-card']}>
            <UnsupportedRuleBanner selectedRule={selectedRule} />
            <MissingWarehouseBanner selectedRule={selectedRule} />
            {!hasAccess && !isNew && (
              <BannerMessageV2
                content={<Text textType={TextType.Paragraph} value={messages.ruleHasLimitedAccess} />}
                header={<Text textType={TextType.Label} value={messages.ruleHasLimitedAccessTitle} />}
                layout={BannerLayout.Full}
                status={BannerStatus.Error}
              />
            )}
            {selectedRule?.isAiGenerated && (
              <div className={style['ai-generated-banner']}>
                <FlexRow className={style['ai-banner-content']} gap={Gap.Space8}>
                  <FontAwesomeIcon className={style['ai-banner-icon']} icon={faSparkles} />
                  <Text className={style['ai-banner-text']} textType={TextType.Paragraph} value={messages.aiGeneratedRuleBanner} />
                </FlexRow>
              </div>
            )}
            {isDrawerLoading && <LoadingIndicator className={style['loading-icon']} />}
            {!isDrawerLoading && <EditorBody />}
          </div>
          <FlexColumn className={style['flow-connector']}>
            <div className={style['flow-connector-line']} />
          </FlexColumn>
          <FlexRow className={style['flow-step-box']} gap={Gap.Space12}>
            <Text textType={TextType.Paragraph} value={messages.flowAutomationComplete} />
            <FontAwesomeIcon className={style['flow-step-check']} icon={faCircleCheck} />
          </FlexRow>
        </FlexColumn>
      </div>
      <FlexRow className={style['drawer-footer']}>
        <Button className={style['delete-rule']} disabled={isNew} onClick={onDeleteRule} theme='link'>
          <Text textType={TextType.Label} value={messages.ruleDeleteLink} />
        </Button>
        <div className={style['button-group']}>
          <Button className={style['cancel-button']} onClick={onCloseDrawer} theme='tertiary'>
            <Text textType={TextType.Label} value={messages.ruleEditorCancel} />
          </Button>
          <SaveButton />
        </div>
      </FlexRow>
    </Drawer>
  );
};
